import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import {
    AbstractControl,
    AsyncValidator,
    ControlValueAccessor,
    NG_ASYNC_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
} from '@angular/forms';
import { ConfigService } from 'app/app-config.service';
import Compressor from 'compressorjs';

export enum FileError {
    FILE_SIZE_EXCEEDED,
    INVALID_FILE_TYPE,
    UNKNOWN_ERROR,
}

@Component({
    selector: 'app-image-selector',
    templateUrl: './image-selector.component.html',
    styleUrls: ['./image-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ImageSelectorComponent,
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            multi: true,
            useExisting: ImageSelectorComponent,
        },
    ],
    standalone: false,
})
export class ImageSelectorComponent implements ControlValueAccessor, AsyncValidator {
    @Input() label?: string;
    @Input() icon?: string = 'ms ms-upload';
    /** Id of the file input, for the `for` of the form label naming this field. */
    @Input() inputId?: string;
    @Input() maxFileSize: number | undefined = 2 * 1024 * 1024; // 2MB
    /**
     * Comma-separated list of pattern to restrict the allowed file types. Can be any combination of either the MIME types (such as "image/*") or the file extensions (such as ".jpg").
     * It is only an indication and will not be validated.
     */
    @Input() accept: string = '*';
    @Input() disabled: boolean = false;
    @Input() style?: CSSStyleDeclaration;
    private _styleClass?: string;
    @Input()
    set styleClass(v: string | undefined) {
        this._styleClass = v === undefined ? undefined : v + ' h-full';
    }
    get styleClass(): string | undefined {
        return this._styleClass;
    }
    @Input() maxWidth: number = 800;
    @Input() maxHeight: number = 800;
    @Input() required: boolean = false;

    @Input() placeholder?: string;

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    onChange = (_: string | null) => {
        // noop — reassigned by registerOnChange
    };

    touched = false;
    onTouched = () => {
        // noop — reassigned by registerOnTouched
    };

    currentValue: string | null = null;
    private currentFile: File | undefined; // For validation purposes

    constructor(public configService: ConfigService) {}

    getRandomColor(index: number): string {
        const colors = this.configService.brand.chartColors;
        return colors[index % colors.length];
    }

    writeValue(obj: string | undefined): void {
        this.currentValue = obj ?? null;
    }

    registerOnChange(onChange: (_: string | null) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    handleFileRequest(_event: MouseEvent) {
        if (this.disabled) {
            return;
        }
        this.fileInput.nativeElement.click();
    }

    async validate(_control: AbstractControl<string>): Promise<ValidationErrors | null> {
        if (this.required && this.currentValue === null) {
            return { required: true };
        }
        if (this.currentValue === null) {
            return null;
        }
        if (this.currentFile === undefined) {
            return null;
        } // failsafe because we never know

        if (this.maxFileSize !== undefined && this.currentFile && this.currentFile.size > this.maxFileSize) {
            return { fileSizeExceeded: true };
        }

        if (!this.isValidFileType(this.currentFile)) {
            return { invalidFileType: true };
        }

        const imageValidationPromise: ValidationErrors | null = await new Promise((res) => {
            const image = new Image();
            image.src = this.currentValue ?? '';
            image.onload = (rs) => {
                if (rs.currentTarget === null) {
                    // validation error
                    res({ unknownError: true });
                    return;
                }

                res(null);
            };
        });
        return imageValidationPromise;
    }

    private isValidFileType(file: File): boolean {
        if (this.accept === '*') {
            return true;
        }

        const acceptedTypes = this.accept.split(',').map((type) => type.trim());

        const mimeTypeMatch = acceptedTypes.some((type) => {
            if (type.endsWith('/*')) {
                const category = type.split('/')[0];
                return file.type.startsWith(category + '/');
            }

            if (!type.startsWith('.')) {
                return file.type === type;
            }
            return false;
        });

        const extensionMatch = acceptedTypes.some((type) => {
            if (type.startsWith('.')) {
                const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
                return fileExt === type.toLowerCase();
            }
            return false;
        });

        return mimeTypeMatch || extensionMatch;
    }

    handleFileUpload(_event: Event) {
        if (this.disabled) {
            return;
        }
        if (this.fileInput.nativeElement.files !== null && this.fileInput.nativeElement.files.length >= 1) {
            const file = this.fileInput.nativeElement.files[0];
            this.currentFile = file;
            this.uploadImage(this.fileInput.nativeElement.files[0]);
        }
    }

    clearImage() {
        if (this.disabled) {
            return;
        }
        const oldVal = this.currentValue;
        this.currentValue = null;
        this.currentFile = undefined;
        this.fileInput.nativeElement.value = ''; // clear file input too
        if (oldVal !== null) {
            this.onChange(null);
        }
    }

    private markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    uploadImage(file: File) {
        new Compressor(file, {
            quality: 0.6,
            maxWidth: this.maxWidth,
            maxHeight: this.maxHeight,
            success: (compressedFile) => {
                const reader = new FileReader();
                reader.onload = (event: ProgressEvent<FileReader>) => {
                    const image = new Image();
                    if (event.target === null || event.target.result === null) {
                        console.error(
                            'event target === null or event target result === null. Should NOT happen at all',
                        );
                        return;
                    }
                    if (typeof event.target.result !== 'string') {
                        console.error('event target result !== string');
                        return;
                    }
                    image.src = event.target.result;
                    image.onload = (rs) => {
                        if (rs.currentTarget === null) {
                            return;
                        }
                        this.currentValue = image.src;
                        this.markAsTouched();
                        this.onChange(this.currentValue);
                    };
                };
                reader.readAsDataURL(compressedFile);
            },
            error: (err) => {
                console.error('Error compressing image:', err);

                const reader = new FileReader();
                reader.onload = (event: ProgressEvent<FileReader>) => {
                    const image = new Image();
                    if (event.target === null || event.target.result === null) {
                        console.error(
                            'event target === null or event target result === null. Should NOT happen at all',
                        );
                        return;
                    }
                    if (typeof event.target.result !== 'string') {
                        console.error('event target result !== string');
                        return;
                    }
                    image.src = event.target.result;
                    image.onload = (rs) => {
                        if (rs.currentTarget === null) {
                            return;
                        }
                        this.currentValue = image.src;
                        this.markAsTouched();
                        this.onChange(this.currentValue);
                    };
                };
                reader.readAsDataURL(file);
            },
        });
    }
}
